import React, { useMemo } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import Image from 'next/image';
import { LuLock } from 'react-icons/lu';
import {
  PhonicsLessonGroup,
  PhonicsLesson,
  PhonicsClassStatus,
} from '@/api/types';
import PupilAvatarSrc from '@/assets/pupil-avatar.png';

/**
 * <div class="bg-pt-coral-red before:bg-pt-coral-red after:bg-pt-coral-red"><div class="bg-pt-cinderella"></div></div>
 * <div class="bg-pt-endeavour before:bg-pt-endeavour after:bg-pt-endeavour"><div class="bg-pt-onahau"></div></div>
 * <div class="bg-pt-lightening-yellow before:bg-pt-lightening-yellow after:bg-pt-lightening-yellow"><div class="bg-pt-golden-glow"></div></div>
 * <div class="bg-pt-pretty-in-pink before:bg-pt-pretty-in-pink after:bg-pt-pretty-in-pink"><div class="bg-pt-faint-pink"></div></div>
 * <div class="bg-pt-trinidad before:bg-pt-trinidad after:bg-pt-trinidad"><div class="bg-pt-faint-orange"></div></div>
 * <div class="bg-pt-affair before:bg-pt-affair after:bg-pt-affair"><div class="bg-pt-mouve"></div></div>
 * <div class="bg-gray-200"><div class=""></div></div>
 **/

type LessonListLessonData = {
  lesson: PhonicsLesson;
  number: number;
  year: string;
  yearIndex: number;
  week: string;
  weekIndex: number;
  isCompleted: boolean;
  isNext: boolean;
};

type LessonListRowData = {
  lessons: Array<LessonListLessonData>;
};

enum TrackColors {
  Red = 'red',
  Blue = 'blue',
  Yellow = 'yellow',
  Pink = 'pink',
  Orange = 'orange',
  Magenta = 'magenta',
}

const TRACK_COLORS = Object.values(TrackColors);

const getTrackColor = (lessonData: LessonListLessonData) =>
  TRACK_COLORS[lessonData.weekIndex % TRACK_COLORS.length];

const getTrackColorClass = (color?: TrackColors, accent = false) => {
  switch (color) {
    case TrackColors.Red:
      return !accent ? 'bg-pt-coral-red' : 'bg-pt-cinderella';
    case TrackColors.Blue:
      return !accent ? 'bg-pt-endeavour' : 'bg-pt-onahau';
    case TrackColors.Yellow:
      return !accent ? 'bg-pt-lightening-yellow' : 'bg-pt-golden-glow';
    case TrackColors.Pink:
      return !accent ? 'bg-pt-pretty-in-pink' : 'bg-pt-faint-pink';
    case TrackColors.Orange:
      return !accent ? 'bg-pt-trinidad' : 'bg-pt-faint-orange';
    case TrackColors.Magenta:
      return !accent ? 'bg-pt-affair' : 'bg-pt-mouve';
    default:
      return !accent ? 'bg-gray-200' : '';
  }
};

const lessonIsCompletedByClass = (
  lessonId: string,
  phonicsClass: PhonicsClassStatus,
) =>
  phonicsClass.classLessonStatuses.find(
    (classLessonStatus) => classLessonStatus.lessonId === lessonId,
  )?.completedAt;

const buildLessonRows = (
  lessonYearGroups: Array<PhonicsLessonGroup>,
  lessonsPerRow = 4,
  phonicsClass: PhonicsClassStatus,
) => {
  const rows: Array<LessonListRowData> = [];

  let currentRow: LessonListRowData = {
    lessons: [],
  };

  lessonYearGroups.forEach((year, yearIndex) => {
    year.children?.forEach((week, weekIndex) => {
      week.lessons?.forEach((lesson) => {
        currentRow.lessons.push({
          lesson,
          number: lesson.number,
          year: year.name,
          yearIndex,
          week: week.name,
          weekIndex,
          isCompleted: lessonIsCompletedByClass(lesson.id, phonicsClass),
          isNext: lesson.id === phonicsClass.currentLessonId,
        });

        if (
          (rows.length === 0 &&
            currentRow.lessons.length === lessonsPerRow - 1) ||
          currentRow.lessons.length === lessonsPerRow
        ) {
          rows.push(currentRow);

          currentRow = {
            lessons: [],
          };
        }
      });
    });
  });

  return rows;
};

interface LessonPodProps {
  lesson: PhonicsLesson;
  trackColor?: TrackColors;
  isCompleted: boolean;
  isNext: boolean;
}

const LessonPod = ({
  lesson,
  trackColor,
  isCompleted,
  isNext,
}: LessonPodProps) => {
  const wrapperClassName = clsx(
    'group/lesson relative w-36 h-24 px-6 my-6',
    'before:block before:absolute before:left-0 before:z-4',
    'before:h-24 before:w-full',
    'group-first/row:first:pl-0 group-first/row:first:w-[120px] group-first/row:first:before:rounded-l-full',
    `before:${getTrackColorClass(trackColor)}`,
    'last:w-36 last:h-72',
    'last:before:h-full last:before:w-full',
    'last:before:border-black last:before:border-solid last:before:rounded-r-full',
    'group-[.in-reverse]/row:last:before:rounded-r-none group-[.in-reverse]/row:last:before:rounded-l-full',
    'last:after:absolute last:after:z-5 last:after:left-0 last:after:top-24',
    'group-[.in-reverse]/row:last:after:left-auto group-[.in-reverse]/row:last:after:right-0',
    'last:after:w-12 last:after:h-24 last:after:bg-pt-green-grass last:after:rounded-r-full',
    'group-[.in-reverse]/row:last:after:rounded-r-none group-[.in-reverse]/row:last:after:rounded-l-full',
  );

  const titleClassName = clsx(
    'relative z-5 flex flex-col justify-center items-center py-3 w-24 h-24',
    'font-bold text-center rounded-full text-gray-600 text-md leading-tight',
    (isCompleted || isNext) && 'hover:bg-opacity-50',
    getTrackColorClass(trackColor, true),
    'group-last/lesson:absolute group-last/lesson:right-0 group-last/lesson:top-24',
    'group-[.in-reverse]/row:right-auto group-[.in-reverse]/row:left-0',
    'group-last/row:group-last/lesson:after:absolute group-last/row:group-last/lesson:after:z-30',
    'group-last/row:group-last/lesson:after:-bottom-36 group-last/row:group-last/lesson:after:-left-52',
    'group-last/row:group-last/lesson:after:w-[185px] group-last/row:group-last/lesson:after:h-[199px]',
    'group-last/row:group-last/lesson:after:bg-present',
  );

  return isCompleted || isNext ? (
    <Link href={`/play/${lesson.id}`} className={wrapperClassName}>
      <h2 className={titleClassName}>
        Lesson
        <br />
        {lesson.number}
        {isNext && (
          <Image
            className={clsx(
              'absolute z-20 -left-6 -translate-x-1/2 -translate-y-1/2',
              'group-[.in-reverse]/row:left-auto group-[.in-reverse]/row:-right-6',
              'group-[.in-reverse]/row:translate-x-1/2',
              'group-last/lesson:-top-20 group-last/lesson:-left-3',
            )}
            src={PupilAvatarSrc}
            alt="Pupil Avatar"
          />
        )}
      </h2>
    </Link>
  ) : (
    <div className={wrapperClassName}>
      <h2 className={titleClassName}>
        <LuLock className="w-6 h-6" />
        Lesson
        <br />
        {lesson.number}
      </h2>
    </div>
  );
};

interface LessonListRowProps {
  row: LessonListRowData;
  index: number;
}

const LessonListRow = ({ row, index }: LessonListRowProps) => (
  <div
    className={clsx(
      'relative h-48 group/row',
      index % 2 === 0 ? '' : 'in-reverse',
      'before:absolute before:top-6 before:w-2 before:h-12 after:shadow',
      index % 2 === 0
        ? 'before:right-6 before:rotate-45'
        : 'before:left-6 before:-rotate-45',
      `before:${getTrackColorClass(getTrackColor(row.lessons[row.lessons.length - 1]))}`,
      'after:absolute after:-top-6 after:w-24 after:h-16 after:px-6',
      'after:flex after:items-center after:justify-center after:font-bold after:text-center',
      `after:${getTrackColorClass(getTrackColor(row.lessons[row.lessons.length - 1]))}`,
      'after:content-[attr(data-week)]',
      index % 2 === 0
        ? 'after:-right-14 after:rotate-45'
        : 'after:-left-14 after:-rotate-45',
    )}
    data-week={`Week ${row.lessons[row.lessons.length - 1].weekIndex + 1}`}
  >
    <div
      className={clsx(
        'flex justify-end h-48',
        index % 2 === 0 ? '' : 'flex-row-reverse',
      )}
    >
      {row.lessons.map((lessonData) => (
        <LessonPod
          key={lessonData.lesson.id}
          lesson={lessonData.lesson}
          trackColor={getTrackColor(lessonData)}
          isCompleted={lessonData.isCompleted}
          isNext={lessonData.isNext}
        />
      ))}
    </div>
  </div>
);

interface LessonListProps {
  rows: Array<LessonListRowData>;
}

const LessonList = ({ rows }: LessonListProps) => (
  <div className={clsx('relative w-[86.4rem] mx-auto pb-24')}>
    {rows.map((row, index) => (
      <LessonListRow key={`row-${index}`} row={row} index={index} />
    ))}
  </div>
);

interface StudentLessonMapProps {
  lessonYearGroups: Array<PhonicsLessonGroup>;
  phonicsClass: PhonicsClassStatus;
}

export const StudentLessonMap = ({
  lessonYearGroups,
  phonicsClass,
}: StudentLessonMapProps) => {
  const rows = useMemo(
    () => buildLessonRows(lessonYearGroups, 5, phonicsClass),
    [lessonYearGroups, phonicsClass],
  );

  return <LessonList rows={rows} />;
};
